import Svg from "@/components/base/Svg"
import { css } from "@emotion/react"
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createLogger } from "@/utils/logger"
import { ModalSlideUpStaticMethods } from "./ModalSlideUp/main"
import BaseTakePhotoV2, { BaseTakePhotoRef } from "./BaseTakePhotoV2"
import styled from "@emotion/styled"

const logger = createLogger("BaseImageUploader")

type Props = {
    label?: string
    value?: string
    onChange?: (value: string) => void
    onUpload?: (file: File) => void
    defaultValue?: string
    disabled?: boolean
    status?: "idle" | "focus" | "error"
    canChange?: boolean
    withCamera?: boolean
    prefix?: React.ReactNode
    inputProps?: Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        "onChange" | "onFocus" | "onBlur" | "value" | "defaultValue" | "disabled" | "ref"
    >
}

export default function BaseImageUploader(props: Props) {
    const {
        //
        value,
        onChange,
        onUpload,
        defaultValue,
        disabled,
        status: _status,
        inputProps: _inputProps,
        withCamera = false,
        canChange = true,
    } = props
    const inputRef = useRef<HTMLInputElement>(null)
    const [status] = useState<NonNullable<typeof _status>>(_status ?? "idle")

    const finalValue = useMemo(() => {
        if (value) {
            // @ts-ignore
            if (value instanceof File) {
                return URL.createObjectURL(value)
            }
            return value
        }
        return defaultValue
    }, [value, defaultValue])
    const finalStatus = useMemo(() => {
        if (disabled) {
            return "idle"
        }
        return _status ?? status
    }, [disabled, _status, status])

    const BoxShadowColor = useMemo(() => {
        switch (finalStatus) {
            case "error":
                return "#FF0000"
            case "focus":
                return "#0A6CFF"
            default:
                return "#e0e0e1"
        }
    }, [finalStatus])

    const ClearButtonColor = useMemo(() => {
        switch (finalStatus) {
            case "error":
                return "#FF0000"
            case "focus":
                return "#0A6CFF"
            default:
                return "#000000"
        }
    }, [finalStatus])

    const BackgroundColor = useMemo(() => {
        switch (finalStatus) {
            case "error":
                return "#ffe0e0"
            case "focus":
                return "#e6f0ff"
            default:
                return "#ffffff"
        }
    }, [finalStatus])

    const PlaceholderColor = useMemo(() => {
        switch (finalStatus) {
            case "error":
                return "#ffadad"
            case "focus":
                return "#96c0ff"
            default:
                return "#c9c9c9"
        }
    }, [finalStatus])

    const canAddOrChange = useMemo(() => {
        if (disabled) {
            return false
        }
        return canChange
    }, [disabled, canChange])

    const [isMounted, setIsMounted] = useState(true)

    useEffect(() => {
        return () => setIsMounted(false)
    }, [])

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            if (!file) {
                return
            }
            const reader = new FileReader()
            reader.onload = (e) => {
                if (!isMounted) {
                    return
                }
                logger.info("handleChange", {
                    file,
                    result: e.target?.result,
                })
                onUpload?.(file)
                onChange?.(e.target?.result as string)
            }
            reader.readAsDataURL(file)
        },
        [isMounted, onChange, onUpload],
    )

    return (
        <div
            css={css`
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                font-family: "Gilroy", sans-serif;
                transition:
                    box-shadow 0.2s ease-in-out,
                    background-color 0.2s ease-in-out;
                flex: 1;
                min-height: 200rem;
                border-radius: 8rem;
                padding: 0 16rem;
                box-shadow: inset 0 0 0 1.5rem ${BoxShadowColor};
                background-color: ${disabled ? "#f0f0f0" : BackgroundColor};
                cursor: ${disabled ? "not-allowed" : "default"};
                user-select: none;
                z-index: 1;
            `}
        >
            <div
                css={css`
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    object-fit: cover;
                    border-radius: 8rem;
                    overflow: hidden;
                    background-color: #f0f0f0;
                `}
            >
                <img
                    css={css`
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    `}
                    src={finalValue}
                />
            </div>
            <input
                css={css`
                    pointer-events: none;
                    user-select: none;
                    position: absolute;
                    opacity: 0;
                `}
                type="file"
                ref={inputRef}
                onChange={handleChange}
                accept={_inputProps?.accept ?? "image/png"}
            />
            {canAddOrChange && (
                <div
                    onClick={() => {
                        if (withCamera) {
                            ModalSlideUpStaticMethods.open(
                                "BaseImageUploader",
                                <CameraScreen
                                    label={props.label}
                                    onFile={(file) => {
                                        onUpload?.(file)
                                        onChange?.(URL.createObjectURL(file))
                                        ModalSlideUpStaticMethods.close("BaseImageUploader")
                                    }}
                                />,
                                {
                                    position: "bottom",
                                    animation: "slide-up",
                                },
                            )
                        } else {
                            inputRef.current?.click()
                        }
                    }}
                    css={css`
                        position: relative;
                        user-select: none;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 2rem;
                        cursor: pointer;
                        z-index: 1;
                    `}
                >
                    <Svg
                        css={css`
                            width: 32rem;
                            height: 32rem;
                        `}
                        src="kyc/add_photo.svg"
                    />
                    <div
                        css={css`
                            font-family: Gilroy;
                            font-weight: 500;
                            font-size: 14rem;
                            line-height: 20rem;
                            letter-spacing: 0%;
                            text-align: center;
                            color: #005ae0;
                        `}
                    >
                        Add
                    </div>
                </div>
            )}
        </div>
    )
}

const CameraScreenContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    align-self: stretch;
    height: 100dvh;
    min-height: 100dvh;
    max-height: 100dvh;
    background-color: #ffffff;
`

const ActionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    margin-bottom: 50rem;
    margin-top: 16rem;
    > svg {
        width: 64rem;
        height: 64rem;
    }
`

function CameraScreen(props: { onFile: (file: File) => void; label?: string }) {
    const ref = useRef<BaseTakePhotoRef>(null)
    const { onFile, label } = props

    const handleTakePhoto = useCallback(() => {
        ref.current?.takePhoto()
    }, [ref])

    const handleCaptureImageFile = useCallback(
        (file: File) => {
            onFile(file)
        },
        [onFile],
    )

    const handleSwitchCamera = useCallback(() => {
        ref.current?.switchCamera()
    }, [ref])

    const inputRef = useRef<HTMLInputElement>(null)

    const handleSelectImage = useCallback(() => {
        inputRef.current?.click()
    }, [])

    return (
        <CameraScreenContainer>
            <div
                css={css`
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <Svg
                    css={css`
                        width: 24rem;
                        height: 24rem;
                        margin: 0 12rem;
                        cursor: pointer;
                    `}
                    src="base_image/back.svg"
                    onClick={() => {
                        ModalSlideUpStaticMethods.close("BaseImageUploader")
                    }}
                />
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        font-family: Gilroy;
                        font-weight: 600;
                        font-size: 16rem;
                        line-height: 20rem;
                        letter-spacing: 0%;
                        text-align: center;
                        color: #005ae0;
                        height: 48rem;
                        margin: auto;
                        white-space: nowrap;
                    `}
                >
                    {label ? `Upload ${label}` : "Upload"}
                </div>
            </div>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: stretch;
                    justify-content: center;
                    min-height: 60%;
                    margin: 0 auto;
                `}
            >
                <BaseTakePhotoV2
                    ref={ref}
                    onCaptureImageFile={handleCaptureImageFile}
                />
            </div>
            <ActionsContainer>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) {
                            return
                        }
                        onFile(file)
                    }}
                />
                <Svg
                    onClick={handleSelectImage}
                    src="kyc/select_image.svg"
                />
                <Svg
                    onClick={handleTakePhoto}
                    src="kyc/button_take_photo.svg"
                />
                <Svg
                    onClick={handleSwitchCamera}
                    src="kyc/rotate.svg"
                />
            </ActionsContainer>
        </CameraScreenContainer>
    )
}
